import { syncedMeasurements } from '../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';

const DEFAULT_POSITION_PADDING = 30;

type MeasurementsKey = keyof typeof syncedMeasurements;
type CanvasScrollPos = { scrollTop: number; scrollLeft: number };

const elementVisibleOrBoardEmpty = (
    canvasElementIds: string[],
    canvasScrollPos: CanvasScrollPos,
    zoomScale: number,
): boolean => {
    if (!canvasElementIds.length) return true;

    const { scrollTop: viewTop, scrollLeft: viewLeft } = canvasScrollPos;

    const canvasViewport = measurementsRegistry.getCanvasViewportDomElement();
    const viewRight = viewLeft + canvasViewport.clientWidth;
    const viewBottom = viewTop + canvasViewport.clientHeight;

    return canvasElementIds.some((canvasElementId) => {
        const element: ElementMeasurement = syncedMeasurements[canvasElementId as MeasurementsKey];
        if (!element) return false;

        const scaledElementLeft = element.left * zoomScale;
        const scaledElementRight = element.right * zoomScale;
        const scaledElementTop = element.top * zoomScale;
        const scaledElementBottom = element.bottom * zoomScale;

        const horizontallyAligned = scaledElementLeft <= viewRight && scaledElementRight >= viewLeft;
        const verticallyAligned = scaledElementTop <= viewBottom && scaledElementBottom >= viewTop;
        return horizontallyAligned && verticallyAligned;
    });
};

export const getElementScrollPosition = (elementId: string, elementPosPx: { x: number; y: number }, zoomScale = 1) => {
    const canvasViewport = measurementsRegistry.getCanvasViewportDomElement();
    if (!canvasViewport) return;

    // On large boards, the element orchestration may still be running so it's possible
    // that the element measurements may not have been set yet in the syncedMeasurements
    const { x, y, width = null } = syncedMeasurements[elementId as MeasurementsKey] || elementPosPx;

    const scaledX = x * zoomScale;
    const scaledY = y * zoomScale;
    const scaledWidth = width ? width * zoomScale : null;

    const shouldCenterElement = scaledWidth && scaledWidth < canvasViewport.clientWidth;
    const centeredPadding = shouldCenterElement ? (canvasViewport.clientWidth - scaledWidth) / 2 : 0;
    const canUseCenteredPadding = centeredPadding && scaledX > centeredPadding && scaledY > centeredPadding;
    const padding = canUseCenteredPadding ? centeredPadding : DEFAULT_POSITION_PADDING;

    return { scrollLeft: scaledX - padding, scrollTop: scaledY - padding };
};

export const getContentVisibleScrollPosition = (
    canvasScrollPos: CanvasScrollPos,
    canvasElementIds: string[],
    dispatchGetTopLeftElementPositionDetails: () => { elementId: string; positionPx: { x: number; y: number } },
    zoomScale: number,
) => {
    if (elementVisibleOrBoardEmpty(canvasElementIds, canvasScrollPos, zoomScale)) {
        return canvasScrollPos;
    }

    const { elementId, positionPx } = dispatchGetTopLeftElementPositionDetails();
    return getElementScrollPosition(elementId, positionPx, zoomScale) || canvasScrollPos;
};
